import type { AuthUseCaseError } from "~application/auth/errors/auth-errors.ts";
import type { AccessTokenProvider } from "~application/auth/ports/access-token-provider.ts";
import type { AuthenticationRepository } from "~application/auth/ports/authentication-repository.ts";
import type { RequiredAccess } from "~domain/auth/models/access-model.ts";
import type { Session } from "~domain/auth/models/session-model.ts";

import { hasAccess } from "~application/auth/policy.ts";
import { InvalidCredentialsError } from "~domain/auth/errors/auth-errors.ts";
import { type Either, Err, Ok } from "~utils/either.ts";

export async function loginAndLoadSession(opts: {
  credentials: { username: string; password: string };
  requiredAccess: RequiredAccess;
  authenticationRepository: AuthenticationRepository;
  tokenProvider: AccessTokenProvider;
}): Promise<Either<Session, AuthUseCaseError>> {
  const { credentials, requiredAccess, authenticationRepository, tokenProvider } = opts;

  try {
    const tokens = await authenticationRepository.login(credentials.username, credentials.password);

    await tokenProvider.saveAccessToken?.(tokens.accessToken);
    await tokenProvider.saveRefreshToken?.(tokens.refreshToken);

    const [operator, rights] = await Promise.all([
      authenticationRepository.fetchMe(),
      authenticationRepository.fetchAccessRights(),
    ]);

    if (!hasAccess(rights, requiredAccess)) {
      await tokenProvider.clear();
      return Err({ kind: "ForbiddenByPolicy", required: requiredAccess });
    }

    return Ok({ operator, rights, tokens });
  } catch (e) {
    console.error(e);

    if (e instanceof InvalidCredentialsError) {
      return Err({ kind: "InvalidCredentials" });
    }

    await tokenProvider.clear();
    return Err({ kind: "UnexpectedError" });
  }
}

export async function bootstrapSession(opts: {
  requiredAccess: RequiredAccess;
  authenticationRepository: AuthenticationRepository;
  tokenProvider: AccessTokenProvider;
}): Promise<Either<Session, AuthUseCaseError>> {
  const { requiredAccess, authenticationRepository, tokenProvider } = opts;
  const tokens = await tokenProvider.get();
  if (!tokens.success) return Err({ kind: "NotAuthenticated" });

  try {
    const probe = await authenticationRepository.ping();
    if (probe === "unauthorized") {
      await tokenProvider.clear();
      return Err({ kind: "NotAuthenticated" });
    }

    const [operator, rights] = await Promise.all([
      authenticationRepository.fetchMe(),
      authenticationRepository.fetchAccessRights(),
    ]);

    if (!hasAccess(rights, requiredAccess)) {
      await tokenProvider.clear();
      return Err({
        kind: "ForbiddenByPolicy",
        required: requiredAccess,
      });
    }

    return Ok({ operator, rights, tokens: tokens.value });
  } catch (e) {
    console.error(e);
    return Err({ kind: "UnexpectedError" });
  }
}
